const SELF_CLOSING = new Set(['br', 'hr']);
export function renderRichTextDocumentToHtml(input) {
    if (input === null || input === undefined) {
        return null;
    }
    if (typeof input === 'string') {
        const trimmed = input.trim();
        if (!trimmed) {
            return '';
        }
        try {
            const parsed = JSON.parse(trimmed);
            return renderParsedRichText(parsed);
        }
        catch {
            return trimmed;
        }
    }
    if (typeof input !== 'object') {
        return null;
    }
    return renderParsedRichText(input);
}
function renderParsedRichText(parsed) {
    if (Array.isArray(parsed)) {
        return parsed.map(renderNode).join('');
    }
    if (!parsed || typeof parsed !== 'object') {
        return '';
    }
    if (parsed.type === 'doc') {
        return renderChildren(parsed.content);
    }
    return renderNode(parsed);
}
function renderChildren(children) {
    if (!Array.isArray(children) || !children.length) {
        return '';
    }
    return children.map(renderNode).join('');
}
function renderNode(node) {
    if (!node || typeof node !== 'object') {
        return '';
    }
    const { type } = node;
    switch (type) {
        case 'doc':
            return renderChildren(node.content);
        case 'paragraph':
            return wrapBlock('p', renderChildren(node.content));
        case 'blockquote':
            return wrapBlock('blockquote', renderChildren(node.content));
        case 'bulletList':
        case 'bullet_list':
            return wrapBlock('ul', renderChildren(node.content));
        case 'orderedList':
        case 'ordered_list':
            return wrapBlock('ol', renderChildren(node.content), buildOrderedListAttrs(node.attrs));
        case 'listItem':
        case 'list_item':
            return wrapBlock('li', renderChildren(node.content));
        case 'taskList':
        case 'taskListNode':
            return wrapBlock('ul', renderChildren(node.content), ' data-type="taskList"');
        case 'taskItem':
        case 'taskItemNode':
            return wrapBlock('li', renderChildren(node.content), buildTaskItemAttrs(node.attrs));
        case 'heading':
            return renderHeading(node);
        case 'codeBlock':
        case 'code_block':
            return wrapBlock('pre', wrapBlock('code', renderChildren(node.content)));
        case 'hardBreak':
        case 'hard_break':
            return '<br />';
        case 'horizontalRule':
        case 'horizontal_rule':
            return '<hr />';
        case 'image':
            return renderImage(node.attrs);
        case 'mention':
            return renderMention(node.attrs);
        case 'text':
            return applyMarks(escapeHtml(node.text ?? ''), node.marks);
        default:
            return renderChildren(node.content);
    }
}
function renderHeading(node) {
    const levelCandidate = (node.attrs?.level ?? node.attrs?.level);
    const level = clampHeadingLevel(levelCandidate);
    const tag = `h${level}`;
    return wrapBlock(tag, renderChildren(node.content));
}
function clampHeadingLevel(value) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
        return Math.min(Math.max(Math.trunc(numeric), 1), 6);
    }
    return 1;
}
function wrapBlock(tag, content, attrs = '') {
    if (SELF_CLOSING.has(tag)) {
        return `<${tag}${attrs ? ` ${attrs.trim()}` : ''} />`;
    }
    const normalizedAttrs = attrs ? ` ${attrs.trim()}` : '';
    return `<${tag}${normalizedAttrs}>${content}</${tag}>`;
}
function buildOrderedListAttrs(attrs) {
    if (!attrs) {
        return '';
    }
    const start = attrs.start ?? attrs.start;
    const numeric = Number(start);
    if (!Number.isFinite(numeric) || numeric <= 1) {
        return '';
    }
    return `start="${numeric}"`;
}
function buildTaskItemAttrs(attrs) {
    if (!attrs) {
        return '';
    }
    const checked = attrs.checked ?? attrs.checked;
    if (checked === undefined || checked === null) {
        return 'data-task-item';
    }
    return `data-task-item data-checked="${checked ? 'true' : 'false'}"`;
}
function renderImage(attrs) {
    if (!attrs) {
        return '';
    }
    const src = typeof attrs.src === 'string' ? attrs.src : '';
    const alt = typeof attrs.alt === 'string' ? attrs.alt : '';
    const title = typeof attrs.title === 'string' ? attrs.title : '';
    const attrParts = [`src="${escapeAttribute(src)}"`];
    if (alt) {
        attrParts.push(`alt="${escapeAttribute(alt)}"`);
    }
    if (title) {
        attrParts.push(`title="${escapeAttribute(title)}"`);
    }
    return `<img ${attrParts.join(' ')} />`;
}
function renderMention(attrs) {
    if (!attrs) {
        return '';
    }
    const label = typeof attrs.label === 'string' ? attrs.label : '';
    const id = typeof attrs.id === 'string' ? attrs.id : '';
    const entity = typeof attrs.entity === 'string' ? attrs.entity : '';
    const type = typeof attrs.type === 'string' ? attrs.type : '';
    const dataAttrs = [];
    if (id) {
        dataAttrs.push(`data-mention-id="${escapeAttribute(id)}"`);
    }
    if (entity) {
        dataAttrs.push(`data-mention-entity="${escapeAttribute(entity)}"`);
    }
    if (type) {
        dataAttrs.push(`data-mention-type="${escapeAttribute(type)}"`);
    }
    const attrsString = dataAttrs.length ? ` ${dataAttrs.join(' ')}` : '';
    const content = label || id;
    return `<span${attrsString}>@${escapeHtml(content)}</span>`;
}
function applyMarks(value, marks) {
    if (!marks?.length) {
        return value;
    }
    return marks.reduce((acc, mark) => {
        const type = mark?.type;
        if (!type) {
            return acc;
        }
        switch (type) {
            case 'bold':
            case 'strong':
                return wrapInline('strong', acc);
            case 'italic':
            case 'em':
                return wrapInline('em', acc);
            case 'strike':
                return wrapInline('s', acc);
            case 'underline':
                return wrapInline('u', acc);
            case 'code':
                return wrapInline('code', acc);
            case 'link':
                return renderLinkMark(acc, mark.attrs);
            default:
                return acc;
        }
    }, value);
}
function wrapInline(tag, value) {
    return `<${tag}>${value}</${tag}>`;
}
function renderLinkMark(value, attrs) {
    if (!attrs) {
        return `<a>${value}</a>`;
    }
    const href = typeof attrs.href === 'string' ? attrs.href : '';
    const target = typeof attrs.target === 'string' ? attrs.target : '';
    const rel = typeof attrs.rel === 'string' ? attrs.rel : '';
    const attrParts = [];
    if (href) {
        attrParts.push(`href="${escapeAttribute(href)}"`);
    }
    if (target) {
        attrParts.push(`target="${escapeAttribute(target)}"`);
    }
    if (rel) {
        attrParts.push(`rel="${escapeAttribute(rel)}"`);
    }
    const attrsString = attrParts.length ? ` ${attrParts.join(' ')}` : '';
    return `<a${attrsString}>${value}</a>`;
}
function escapeHtml(value) {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
function escapeAttribute(value) {
    return escapeHtml(value);
}
/**
 * Extracts unique mentions from rich-text JSON (object/array) or its stringified form
 */
export function extractMentionsFromRichText(rawInput) {
    let parsedInput = rawInput;
    if (typeof parsedInput === 'string') {
        const s = parsedInput.trim();
        if (!s)
            return [];
        try {
            parsedInput = JSON.parse(s);
        }
        catch {
            return [];
        }
    }
    const stack = Array.isArray(parsedInput) ? [...parsedInput] : [parsedInput];
    const mentions = [];
    while (stack.length) {
        const node = stack.pop();
        if (node.type === 'mention') {
            const { id, entity } = node.attrs;
            mentions.push({ entityId: id, entityName: entity });
        }
        if (node.content?.length)
            stack.push(...node.content);
    }
    const seen = new Set();
    return mentions.filter((m) => (seen.has(m.entityId) ? false : (seen.add(m.entityId), true)));
}
